import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BarChart3, Camera, CreditCard, ExternalLink, Loader2, Shield, SlidersHorizontal, Trash2, User, Wrench, X } from "lucide-react";
import { API, useAuth } from "@/App";
import { tokenStore } from "@/lib/token";
import UserAvatar from "@/components/UserAvatar";
import { errorText, resetIn, vai } from "@/lib/vai";
import { LANGUAGES } from "@/components/vai/AccountMenu";
import { confirmDialog } from "@/components/vai/Dialogs";
import useClosing from "@/lib/useClosing";

// Réglages de Vanta AI (fenêtre, comme sur claude.ai) : général (profil du site et
// langue des réponses), capacités, abonnement, utilisation, confidentialité.

const SECTIONS = [
  { id: "general", label: "Général", icon: User },
  { id: "capabilities", label: "Capacités", icon: Wrench },
  { id: "billing", label: "Abonnement", icon: CreditCard },
  { id: "usage", label: "Utilisation", icon: BarChart3 },
  { id: "privacy", label: "Confidentialité", icon: Shield },
];

function Toggle({ checked, onChange, testId }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testId}
      className={`relative inline-flex items-center w-11 h-6 p-0.5 rounded-full transition-colors duration-200 shrink-0 ${checked ? "bg-blue-500" : "bg-white/15"}`}>
      <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ease-out ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

const COLORS = ["#3B82F6", "#8B5CF6", "#EC4899", "#EF4444", "#F59E0B", "#10B981", "#06B6D4", "#64748B"];
const STYLES = [["flat", "Uni"], ["matte", "Mat"], ["metallic", "Métallisé"]];

// Profil du site (le même que dans Paramètres du compte) : pseudo et photo. Vanta AI
// t'appelle par ce pseudo.
function ProfileEditor() {
  const { user, profile, refreshProfile } = useAuth();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (profile && !draft) setDraft({ username: profile.username || user?.username || "", avatar_type: profile.avatar_type || "color",
      avatar_color: profile.avatar_color || COLORS[0], avatar_photo: profile.avatar_photo || null, avatar_style: profile.avatar_style || "flat" });
  }, [profile, user, draft]);
  if (!draft) return <p className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement du profil…</p>;

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image trop lourde (2 Mo maximum)"); return; }
    const reader = new FileReader();
    reader.onload = () => set({ avatar_photo: reader.result, avatar_type: "photo" });
    reader.readAsDataURL(file);
  };
  const dirty = !profile || draft.username !== (profile.username || "") || draft.avatar_type !== (profile.avatar_type || "color")
    || draft.avatar_color !== (profile.avatar_color || COLORS[0]) || draft.avatar_photo !== (profile.avatar_photo || null) || draft.avatar_style !== (profile.avatar_style || "flat");
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/update`, {
        username: draft.username, avatar_type: draft.avatar_type, avatar_color: draft.avatar_color, avatar_gradient: [draft.avatar_color],
        avatar_style: draft.avatar_style, avatar_photo: draft.avatar_type === "photo" ? draft.avatar_photo : "",
      }, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
      await refreshProfile();
      toast.success("Profil enregistré");
    } catch (err) { toast.error(err?.response?.data?.detail || "Enregistrement impossible"); }
    setSaving(false);
  };

  const preview = { ...draft, avatar_gradient: [draft.avatar_color] };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4" data-testid="vai-profile">
      <div className="flex items-center gap-4">
        <div className="relative shrink-0">
          <UserAvatar profile={preview} size={64} />
          <button type="button" onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-white text-black shadow hover:scale-105 transition-transform" aria-label="Changer la photo">
            <Camera className="w-3.5 h-3.5" />
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={pickPhoto} />
        </div>
        <div className="min-w-0 flex-1">
          <label className="block text-xs text-white/50 mb-1" htmlFor="vai-username">Pseudo (Vanta AI t'appelle comme ça)</label>
          <input id="vai-username" value={draft.username} onChange={(e) => set({ username: e.target.value })} maxLength={30}
            className="w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/25" data-testid="vai-settings-username" />
        </div>
      </div>
      <div>
        <p className="text-xs text-white/50 mb-2">Photo de profil</p>
        <div className="flex flex-wrap items-center gap-2">
          {COLORS.map((c) => (
            <button key={c} type="button" onClick={() => set({ avatar_color: c, avatar_type: "color" })} aria-label={`Couleur ${c}`}
              className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${draft.avatar_type === "color" && draft.avatar_color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#121218]" : ""}`} style={{ background: c }} />
          ))}
          {draft.avatar_photo && draft.avatar_type === "photo" && (
            <button type="button" onClick={() => set({ avatar_type: "color", avatar_photo: null })} className="ml-1 text-xs text-white/55 hover:text-white flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Retirer la photo</button>
          )}
        </div>
        {draft.avatar_type === "color" && (
          <div className="flex gap-1.5 mt-3">
            {STYLES.map(([id, label]) => (
              <button key={id} type="button" onClick={() => set({ avatar_style: id })}
                className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${draft.avatar_style === id ? "border-white/40 bg-white/10 text-white" : "border-white/10 text-white/55 hover:text-white"}`}>{label}</button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/settings" className="text-xs text-sky-300 hover:underline flex items-center gap-1">Tous les réglages du compte<ExternalLink className="w-3 h-3" /></Link>
        <button type="button" disabled={!dirty || saving} onClick={save} className="px-4 py-2 rounded-lg bg-white text-black text-sm font-medium disabled:opacity-40 transition-opacity" data-testid="vai-settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

function Row({ title, help, children }) {
  return (
    <div className="flex items-center justify-between gap-6 py-4 border-b border-white/[0.06] last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        {help && <p className="text-xs text-white/45 mt-0.5">{help}</p>}
      </div>
      {children}
    </div>
  );
}

export function UsageBar({ label, used, limit, resetsAt, testId }) {
  const pct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  return (
    <div className="py-3" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-4 text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-white/50 text-xs">{pct} % utilisé{resetsAt ? ` · se recharge ${resetIn(resetsAt)}` : ""}</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-red-400" : pct >= 70 ? "bg-amber-400" : "bg-gradient-to-r from-blue-500 to-violet-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function SettingsDialog({ me, initial = "general", onClose: done, onChanged, onUpgrade, onDeletedAll }) {
  const [section, setSection] = useState(initial);
  const [closing, onClose] = useClosing(done);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const save = async (patch, message = "Enregistré") => {
    try {
      const settings = await vai.put("/settings", patch);
      onChanged({ ...me, settings });
      toast.success(message);
    } catch (err) { toast.error(errorText(err)); }
  };
  const cancel = async (resume) => {
    if (!resume && !await confirmDialog({ title: `Annuler l'abonnement ${me.plan.name} ?`, text: "Il reste actif jusqu'à la fin du mois payé.", confirmLabel: "Annuler l'abonnement", danger: true })) return;
    setBusy(true);
    try {
      const r = await vai.post(resume ? "/subscription/resume" : "/subscription/cancel");
      onChanged({ ...me, plan: r.plan });
      toast.success(resume ? "Abonnement repris" : "Abonnement annulé à la fin du mois payé");
    } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  const deleteAll = async () => {
    if (!await confirmDialog({ title: "Supprimer toutes tes conversations ?", text: "C'est définitif : elles ne pourront pas être récupérées.", confirmLabel: "Tout supprimer", danger: true })) return;
    try { const r = await vai.del("/chats"); toast.success(`${r.deleted} conversation(s) supprimée(s)`); onDeletedAll(); } catch (err) { toast.error(errorText(err)); }
  };
  const renews = me.plan.renews_at ? new Date(me.plan.renews_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className={`vai-overlay fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-0 sm:p-6 ${closing ? "is-closing" : ""}`} onMouseDown={onClose} role="dialog" aria-modal="true" aria-label="Réglages">
      <div className="vai-dialog w-full h-full sm:h-[min(640px,90vh)] sm:max-w-3xl sm:rounded-2xl border border-white/10 bg-[#121218] shadow-2xl flex flex-col sm:flex-row overflow-hidden" onMouseDown={(e) => e.stopPropagation()} data-testid="vai-settings">
        <nav className="sm:w-52 shrink-0 border-b sm:border-b-0 sm:border-r border-white/[0.07] p-3 flex sm:flex-col gap-1 overflow-x-auto">
          <div className="hidden sm:flex items-center justify-between px-2 pb-3"><span className="font-semibold">Réglages</span></div>
          {SECTIONS.map((s) => (
            <button key={s.id} type="button" onClick={() => setSection(s.id)} data-testid={`vai-settings-${s.id}`}
              className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm whitespace-nowrap ${section === s.id ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <s.icon className="w-4 h-4" />{s.label}
            </button>
          ))}
          <button type="button" onClick={onClose} className="sm:hidden ml-auto p-2 rounded-lg text-white/60 hover:bg-white/5" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </nav>
        <section key={section} className="vai-rise flex-1 min-w-0 overflow-y-auto p-5 sm:p-6 relative">
          <button type="button" onClick={onClose} className="hidden sm:block absolute top-4 right-4 p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5" aria-label="Fermer"><X className="w-4 h-4" /></button>

          {section === "general" && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Général</h2>
              <p className="text-sm font-medium mb-2">Profil</p>
              <ProfileEditor />
              <label className="block text-sm font-medium mt-6 mb-1" htmlFor="vai-lang">Langue des réponses</label>
              <p className="text-xs text-white/45 mb-2">Vanta AI répond toujours dans cette langue. En automatique : celle de ton message.</p>
              <select id="vai-lang" value={me.settings.language || "auto"} onChange={(e) => save({ language: e.target.value }, "Langue des réponses enregistrée")}
                className="w-full sm:w-64 rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/25" data-testid="vai-settings-language">
                {LANGUAGES.map(([id, label]) => <option key={id} value={id} className="bg-[#121218]">{id === "auto" ? "Automatique (langue de ton message)" : label}</option>)}
              </select>
            </div>
          )}

          {section === "capabilities" && (
            <div>
              <h2 className="text-lg font-semibold mb-2">Capacités</h2>
              <Row title="Artefacts" help="Pages web, composants, documents, diagrammes et scènes 3D affichés dans un panneau à côté de la conversation.">
                <Toggle checked={me.settings.artifacts} onChange={(v) => save({ artifacts: v })} testId="vai-toggle-artifacts" />
              </Row>
              <Row title="Recherche web automatique" help="Vanta AI cherche sur internet quand la question en a besoin (actualité, documentation…).">
                <Toggle checked={me.settings.search} onChange={(v) => save({ search: v })} testId="vai-toggle-search" />
              </Row>
              <Row title="Mémoire" help="Vanta AI se souvient de tes autres conversations pour mieux te répondre. Supprimer une conversation efface ce qu'il en a retenu.">
                <Toggle checked={me.settings.memory !== false} onChange={(v) => save({ memory: v }, v ? "Mémoire activée" : "Mémoire désactivée")} testId="vai-toggle-memory" />
              </Row>
              <p className="text-xs text-white/40 mt-4">Outils : bouton + › « Auto » (Vanta AI choisit) ou « Manuel » (tu choisis recherche, image ou 3D). La 3D est dans les offres Pro et Max.</p>
            </div>
          )}

          {section === "billing" && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Abonnement</h2>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-white/50">Offre actuelle</p>
                    <p className="text-xl font-semibold mt-0.5" data-testid="vai-current-plan">{me.plan.name}</p>
                    <p className="text-xs text-white/45 mt-1">
                      {me.plan.id === "free" ? "Gratuit, sans engagement." : me.plan.cancel_at_period_end ? `Se termine le ${renews}.` : me.plan.interval === "year" ? `${String(me.plan.price_year).replace(".", ",")} € par an · renouvellement le ${renews}.` : `${String(me.plan.price).replace(".", ",")} € par mois · renouvellement le ${renews}.`}
                    </p>
                  </div>
                  {me.plan.id !== "max" && <button type="button" onClick={onUpgrade} className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-violet-600 text-sm font-medium hover:brightness-110">Changer d'offre</button>}
                </div>
                <ul className="mt-4 grid sm:grid-cols-2 gap-1.5 text-xs text-white/60">{me.plan.features.map((f) => <li key={f}>✓ {f}</li>)}</ul>
              </div>
              {me.plan.managed_by_stripe && (
                <div className="mt-4">
                  {me.plan.cancel_at_period_end
                    ? <button type="button" disabled={busy} onClick={() => cancel(true)} className="text-sm px-3 py-2 rounded-lg border border-white/15 hover:bg-white/5">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reprendre l'abonnement"}</button>
                    : <button type="button" disabled={busy} onClick={() => cancel(false)} className="text-sm px-3 py-2 rounded-lg border border-red-400/30 text-red-200 hover:bg-red-500/10" data-testid="vai-cancel-sub">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Annuler l'abonnement"}</button>}
                </div>
              )}
            </div>
          )}

          {section === "usage" && (
            <div>
              <h2 className="text-lg font-semibold mb-1">Utilisation</h2>
              <p className="text-xs text-white/45 mb-3">Chaque message compte 1, une image 3 et la création 3D 10. Les limites se rechargent toutes seules.</p>
              <UsageBar label="Session en cours (5 heures)" used={me.usage.session.used} limit={me.usage.session.limit} resetsAt={me.usage.session.resets_at} testId="vai-usage-session" />
              <UsageBar label="Cette semaine" used={me.usage.weekly.used} limit={me.usage.weekly.limit} resetsAt={me.usage.weekly.resets_at} testId="vai-usage-weekly" />
              {me.plan.id !== "max" && <button type="button" onClick={onUpgrade} className="mt-3 text-sm text-sky-300 hover:underline">Besoin de plus ? Passer à {me.plan.id === "free" ? "Pro" : "Max"}</button>}
            </div>
          )}

          {section === "privacy" && (
            <div>
              <h2 className="text-lg font-semibold mb-2">Confidentialité</h2>
              <p className="text-sm text-white/60">Tes conversations restent sur les serveurs de VantaPulse : Pulse et Vector tournent sur notre propre serveur, sans service d'une autre entreprise.</p>
              <p className="text-sm text-white/60 mt-2">Mémoire : Vanta AI relit tes conversations gardées pour mieux te répondre (désactivable dans Capacités). Une conversation supprimée est oubliée.</p>
              <Row title="Supprimer toutes les conversations" help="Efface définitivement l'historique de Vanta AI.">
                <button type="button" onClick={deleteAll} className="text-sm px-3 py-2 rounded-lg border border-red-400/30 text-red-200 hover:bg-red-500/10 shrink-0" data-testid="vai-delete-all">Tout supprimer</button>
              </Row>
              <p className="text-xs text-white/40 mt-3 flex items-start gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5 mt-0.5 shrink-0" />Les demandes illégales ou dangereuses sont refusées et peuvent être vérifiées par l'équipe VantaPulse.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
